/**
 * Die Scroll-Bahn: Eingabeflaeche und Anzeige in einem.
 *
 * Aufbau in drei Ebenen:
 *   1. Ein natives Scroll-Element mit leerem, sehr hohem Inhalt. Es liefert das
 *      gewohnte Scroll-Gefuehl samt Nachrollen - genau das soll gemessen werden.
 *      Sichtbar ist daran nichts.
 *   2. Ein Canvas darueber, das das Massband zeichnet. Es haengt allein am
 *      Messwert, nicht an der Scroll-Position (siehe drawTape.ts).
 *   3. Die Anzeige der Disziplin als React-Kinder, ohne Zeigerereignisse, damit
 *      Wische ungehindert zur Bahn durchkommen.
 */

import { useEffect, useRef, type ReactNode } from 'react'
import type { DeviceMetrics } from '../engine/dpi'
import { ScrollTracker, TRACK_HEIGHT_PX } from '../engine/scrollTracker'
import { cmPerCssPx } from '../engine/units'
import { DEFAULT_PALETTE, drawTape, type TapeMarker } from './drawTape'

/**
 * Marken werden entweder als feste Liste angegeben (Ziellinie, Bestweite) oder
 * als Funktion, die pro Bild neu gefragt wird. Letzteres traegt den
 * Schattenlaeufer im Duell: eine Marke, die sich waehrend des Laufs bewegt.
 */
export type MarkerSource = TapeMarker[] | (() => TapeMarker[])

interface ScrollTrackProps {
  metrics: DeviceMetrics
  /** Wird einmal nach dem Aufbau mit dem fertigen Tracker aufgerufen. */
  onTracker: (tracker: ScrollTracker) => void
  /** Zusaetzliche Linien auf der Bahn, etwa das Sprintziel. */
  markers?: MarkerSource
  /** Hoehe der Messlinie als Anteil der Bahnhoehe. */
  referenceRatio?: number
  children?: ReactNode
}

export function ScrollTrack({
  metrics,
  onTracker,
  markers = [],
  referenceRatio = 0.3,
  children,
}: ScrollTrackProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Ueber Refs gelesen, damit sich aendernde Marker nicht den Tracker neu
  // aufbauen - das wuerde mitten im Lauf die Messung zuruecksetzen.
  const markersRef = useRef(markers)
  markersRef.current = markers
  const onTrackerRef = useRef(onTracker)
  onTrackerRef.current = onTracker

  useEffect(() => {
    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return

    const tracker = new ScrollTracker(container, metrics)
    onTrackerRef.current(tracker)

    const ctx = canvas.getContext('2d')
    const pxPerCm = 1 / cmPerCssPx(metrics.dpr, metrics.ppi)
    let width = 0
    let height = 0

    const resize = (): void => {
      const rect = container.getBoundingClientRect()
      width = rect.width
      height = rect.height
      // Interne Aufloesung an die Geraetepixel koppeln, sonst wirken Linien und
      // Schrift auf hochaufloesenden Displays unscharf.
      canvas.width = Math.round(width * metrics.dpr)
      canvas.height = Math.round(height * metrics.dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx?.setTransform(metrics.dpr, 0, 0, metrics.dpr, 0, 0)
    }

    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(container)

    let frame = 0
    const render = (): void => {
      if (ctx && width > 0 && height > 0) {
        drawTape({
          ctx,
          width,
          height,
          forwardCm: tracker.forwardCm,
          pxPerCm,
          referenceRatio,
          markers:
            typeof markersRef.current === 'function' ? markersRef.current() : markersRef.current,
          palette: DEFAULT_PALETTE,
        })
      }
      frame = requestAnimationFrame(render)
    }
    frame = requestAnimationFrame(render)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      tracker.destroy()
    }
    // Nur die Geraetekennwerte duerfen einen Neuaufbau ausloesen.
  }, [metrics, referenceRatio])

  return (
    <div className="track">
      <div ref={containerRef} className="track__surface">
        <div className="track__spacer" style={{ height: `${TRACK_HEIGHT_PX}px` }} />
      </div>
      <canvas ref={canvasRef} className="track__canvas" aria-hidden="true" />
      <div className="track__hud">{children}</div>
    </div>
  )
}
